import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ApiService from '../services/api';
import logger from '../utils/logger';
import { useAdminRole } from './AdminRoleContext';

const AdminDataContext = createContext();

// Cache duration in milliseconds (2 minutes)
const CACHE_DURATION = 2 * 60 * 1000;

export const AdminDataProvider = ({ children }) => {
    // Get role-based filtering parameters
    const { getFilterParams } = useAdminRole();

    const [data, setData] = useState({
        employees: null,
        leads: null,
        clients: null,
        meetings: null,
    });
    const [loading, setLoading] = useState({
        employees: false,
        clients: false,
        meetings: false,
    });
    const [error, setError] = useState({
        employees: null,
        clients: null,
        meetings: null,
    });

    // Track when data was last fetched to avoid unnecessary reloads
    const lastFetched = useRef({
        employees: 0,
        clients: 0,
        meetings: 0,
    });

    // Track if a fetch is in progress to prevent duplicate calls
    const fetchInProgress = useRef({
        employees: false,
        clients: false,
        meetings: false,
    });

    const isCacheValid = (key) => {
        const elapsed = Date.now() - lastFetched.current[key];
        return elapsed < CACHE_DURATION;
    };

    const fetchEmployees = useCallback(async (refresh = false) => {
        // Return cached data if valid and not forcing refresh
        if (!refresh && data.employees && isCacheValid('employees')) {
            return data.employees;
        }

        // Prevent duplicate concurrent fetches
        if (fetchInProgress.current.employees) {
            return data.employees;
        }

        fetchInProgress.current.employees = true;
        setLoading(prev => ({ ...prev, employees: true }));

        try {
            const response = await ApiService.get('/admin/employee/get-employees?pageSize=1000');
            const employeesData = response?.data?.employees || response?.employees || [];
            setData(prev => ({ ...prev, employees: employeesData }));
            lastFetched.current.employees = Date.now();
            return employeesData;
        } catch (err) {
            logger.error('Failed to fetch employees', err);
            setError(prev => ({ ...prev, employees: err.message }));
            return data.employees || null;
        } finally {
            setLoading(prev => ({ ...prev, employees: false }));
            fetchInProgress.current.employees = false;
        }
    }, [data.employees]);

    const fetchClients = useCallback(async (refresh = false) => {
        // Return cached data if valid and not forcing refresh
        if (!refresh && data.clients && isCacheValid('clients')) {
            return data.clients;
        }

        // Prevent duplicate concurrent fetches
        if (fetchInProgress.current.clients) {
            return data.clients;
        }

        fetchInProgress.current.clients = true;
        setLoading(prev => ({ ...prev, clients: true }));

        try {
            // Calculate role params first
            const roleParams = getFilterParams ? getFilterParams() : '';

            // Sequential fetch to avoid ERR_INSUFFICIENT_RESOURCES
            const usersResp = await ApiService.get('/admin/users/get-users').catch(() => ({ data: [] }));

            // Use roleParams for GMB accounts (remove leading '&' and use '?')
            const gmbQuery = roleParams ? `?${roleParams.substring(1)}` : '';
            const accountsResp = await ApiService.get(`/admin/gmb-account/all${gmbQuery}`).catch(() => ({ data: [] }));

            const planResp = await ApiService.get('/admin/gmb-account/debug-plans').catch(() => ({ data: {} }));
            const requestsResp = await ApiService.get('/admin/gmb-account/phone-change-requests').catch(() => ({ data: [] }));
            // Fetch leads assigned to this user so we can bind only their clients
            const leadsResp = await ApiService.get(`/admin/leads/get-leads?pageSize=1000${roleParams}`).catch(() => ({ data: [] }));

            const usersList = usersResp?.data?.users || usersResp?.users || usersResp?.data || [];
            const userMap = {};
            usersList.forEach(u => { if (u?.id) userMap[String(u.id)] = u; });

            // Create lead map by user_id, email, and business_name for matching
            const leadsList = leadsResp?.data?.leads || leadsResp?.leads || leadsResp?.data || [];
            const leadByUserId = {};
            const leadByEmail = {};
            const leadByBusiness = {};
            leadsList.forEach(l => {
                if (l?.user_id) leadByUserId[String(l.user_id)] = l;
                if (l?.email) leadByEmail[String(l.email).toLowerCase()] = l;
                if (l?.contact_email) leadByEmail[String(l.contact_email).toLowerCase()] = l;
                if (l?.business_name) leadByBusiness[String(l.business_name).toLowerCase().trim()] = l;
            });

            const accounts = accountsResp?.data?.accounts || accountsResp?.accounts || accountsResp?.data || [];
            const planRoot = planResp?.data ?? planResp;
            const requests = requestsResp?.data?.requests || requestsResp?.requests || [];

            const normalizeId = (id) => String(id || '').replace('locations/', '');

            const basic = new Set((planRoot?.tierTables?.basic || []).map(p => normalizeId(p.location_id)));
            const advance = new Set((planRoot?.tierTables?.advance || []).map(p => normalizeId(p.location_id)));
            const pro = new Set((planRoot?.tierTables?.pro || []).map(p => normalizeId(p.location_id)));

            const processed = accounts.map(a => {
                const locationId = a.location_id || a.locationId;
                const pureLocId = normalizeId(locationId);
                const plan = pro.has(pureLocId) ? 'pro' : (advance.has(pureLocId) ? 'advance' : (basic.has(pureLocId) ? 'basic' : (a.plan || null)));

                const ownerId = String(a.user?.id || a.user_id || '');
                const accountType = userMap[ownerId]?.account_type || a.user?.account_type || 'User';
                const ownerEmail = a.user?.email || a.email || '';

                // Find matching lead to get lead_uid
                const businessName = (a.business_name || a.location_name || '').toLowerCase().trim();
                let matchingLead = leadByUserId[ownerId] ||
                    (ownerEmail ? leadByEmail[ownerEmail.toLowerCase()] : null) ||
                    (businessName ? leadByBusiness[businessName] : null);
                const lead_uid = matchingLead?.lead_uid || null;

                const phoneReq = requests.find(q => String(q.location_id) === String(locationId) || String(q.id) === String(a.id));

                return {
                    id: a.id,
                    locationId,
                    business_name: a.business_name || a.location_name || '—',
                    email: a.user?.email || a.email || matchingLead?.email || '—',
                    phone_number: a.phone_number || '—',
                    city: a.city || '—',
                    isVerified: a.is_verified || false,
                    plan,
                    userId: ownerId,
                    account_type: accountType,
                    phoneChangeRequest: phoneReq || null,
                    lead_uid,  // ESC Client ID
                    lead: matchingLead || null,  // Full lead record
                    raw: a
                };
            });

            // Track which leads are already associated with a GMB account
            const matchedLeadIds = new Set(processed.filter(acc => acc.lead).map(acc => acc.lead.id));

            // Find "Client" leads that don't have linked GMB accounts
            const unmatchedClientLeads = leadsList.filter(l =>
                (l.type === 'client' || (l.lead_uid && l.lead_uid.startsWith('ESC'))) &&
                !matchedLeadIds.has(l.id)
            ).map(l => {
                const ownerId = String(l.user_id || '');
                const user = userMap[ownerId];
                return {
                    id: `lead-${l.id}`,
                    locationId: null,
                    business_name: l.business_name || '—',
                    email: l.email || user?.email || '—',
                    phone_number: l.phone_number || '—',
                    city: l.city || '—',
                    isVerified: false,
                    plan: l.package || null,
                    userId: ownerId,
                    account_type: user?.account_type || 'User',
                    phoneChangeRequest: null,
                    lead_uid: l.lead_uid,
                    lead: l,
                    raw: null
                };
            });

            const allClients = [...processed.filter(acc => acc.lead), ...unmatchedClientLeads];

            // De-duplicate
            const deDuplicated = [];
            const seenIds = new Set();
            [...allClients].sort((a, b) => (a.plan ? -1 : 1)).forEach(acc => {
                const id = acc.locationId ? normalizeId(acc.locationId) : acc.id;
                if (!seenIds.has(id)) { seenIds.add(id); deDuplicated.push(acc); }
            });

            setData(prev => ({ ...prev, clients: deDuplicated }));
            lastFetched.current.clients = Date.now();
            return deDuplicated;
        } catch (err) {
            logger.error('Failed to fetch clients', err);
            setError(prev => ({ ...prev, clients: err.message }));
            return data.clients || null;
        } finally {
            setLoading(prev => ({ ...prev, clients: false }));
            fetchInProgress.current.clients = false;
        }
    }, [data.clients, getFilterParams]);

    const fetchMeetings = useCallback(async (refresh = false) => {
        // Return cached data if valid and not forcing refresh
        if (!refresh && data.meetings && isCacheValid('meetings')) {
            return data.meetings;
        }

        // Prevent duplicate concurrent fetches
        if (fetchInProgress.current.meetings) {
            return data.meetings;
        }

        fetchInProgress.current.meetings = true;
        setLoading(prev => ({ ...prev, meetings: true }));
        setError(prev => ({ ...prev, meetings: null }));

        try {
            // Apply role-based filtering for BDM Sales (outside/inside) / CRO
            const roleParams = getFilterParams ? getFilterParams() : '';
            const url = `/admin/meeting/get-meeting?pageSize=100${roleParams}`;
            const resp = await ApiService.get(url);
            const root = resp?.data ?? resp;
            const meetings = Array.isArray(root) ? root : (root?.meetings || root?.data?.meetings || root?.data || []);

            setData(prev => ({ ...prev, meetings }));
            lastFetched.current.meetings = Date.now();
            return meetings;
        } catch (err) {
            logger.error('Error fetching meetings:', err);
            setError(prev => ({ ...prev, meetings: err.message }));
            return data.meetings || [];
        } finally {
            setLoading(prev => ({ ...prev, meetings: false }));
            fetchInProgress.current.meetings = false;
        }
    }, [data.meetings, getFilterParams]);

    // Invalidate cache for a specific data type (useful after mutations)
    const invalidateCache = useCallback((type) => {
        if (type === 'all') {
            lastFetched.current = { employees: 0, clients: 0, meetings: 0 };
        } else if (lastFetched.current[type] !== undefined) {
            lastFetched.current[type] = 0;
        }
    }, []);

    const actions = React.useMemo(() => ({
        fetchEmployees,
        fetchClients,
        fetchMeetings,
        invalidateCache
    }), [fetchEmployees, fetchClients, fetchMeetings, invalidateCache]);

    const value = React.useMemo(() => ({
        employees: data.employees,
        leads: data.leads,
        clients: data.clients,
        meetings: data.meetings,
        loading,
        error,
        actions
    }), [data, loading, error, actions]);

    return (
        <AdminDataContext.Provider value={value}>
            {children}
        </AdminDataContext.Provider>
    );
};

export const useAdminData = () => {
    const context = useContext(AdminDataContext);
    if (!context) throw new Error('useAdminData must be used within AdminDataProvider');
    return context;
};
